import { gql, useQuery } from "@apollo/client";
import { useCallback, useMemo } from "react";
import type { Service, ServiceCategory } from "@/gql/graphql";
import { logError, StandardError, transformApolloError } from "@/lib/error/error-utils";
import { useSalonStore } from "@/lib/store/salon";
import type { ServiceRowData } from "../types";

const SERVICES_WITH_CATEGORIES_QUERY = gql`
  query ServicesWithCategories($salonId: ID!) {
    serviceCategories(salonId: $salonId) {
      id
      name
      description
      color
      icon
      sortOrder
      isArchived
      allowOnlineBooking
      createdAt
      updatedAt
    }
    services(salonId: $salonId) {
      id
      name
      description
      serviceCode
      duration
      pricingMode
      basePrice
      isActive
      allowOnlineBooking
      categoryId
      createdAt
      updatedAt
      category {
        id
        name
        color
      }
      displayPrice {
        mode
        fixedPrice
        priceRange {
          minPrice
          maxPrice
        }
        requiresQuote
      }
      serviceOptionGroups {
        id
        name
        selectionType
        options {
          id
          optionName
          priceModifier
          durationModifier
        }
      }
    }
  }
`;

interface UseServicesQueryProps {
  salonId?: string;
}

interface UseServicesQueryReturn {
  categories: ServiceCategory[];
  services: Service[];
  servicesByCategory: Record<string, ServiceRowData[]>;
  loading: boolean;
  error: StandardError | null;
  retry: () => void;
  refetch: () => Promise<any>;
}

/**
 * Query hook for fetching services and categories with standardized error handling
 */
export const useServicesQuery = ({
  salonId,
}: UseServicesQueryProps = {}): UseServicesQueryReturn => {
  const { currentSalon } = useSalonStore();
  const effectiveSalonId = salonId || currentSalon?.id;

  const {
    data,
    loading,
    error: apolloError,
    refetch: apolloRefetch,
  } = useQuery(SERVICES_WITH_CATEGORIES_QUERY, {
    variables: {
      salonId: effectiveSalonId || "",
    },
    skip: !effectiveSalonId,
    errorPolicy: "all",
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  // Extract and transform data
  const categories: ServiceCategory[] = useMemo(() => {
    return data?.serviceCategories || [];
  }, [data?.serviceCategories]);

  const services: Service[] = useMemo(() => {
    return data?.services || [];
  }, [data?.services]);

  // Transform services into ServiceRowData and group by category
  const servicesByCategory = useMemo(() => {
    const sortedCategories = [...categories].sort((a, b) => a.sortOrder - b.sortOrder);

    return sortedCategories.reduce(
      (acc, category) => {
        const categoryServices = services
          .filter((service: Service) => service.category?.id === category.id)
          .map(
            (service: Service): ServiceRowData => ({
              id: service.id,
              name: service.name,
              description: service.description ?? undefined,
              duration: service.duration,
              pricingMode: service.pricingMode,
              basePrice: service.basePrice ?? undefined,
              minPrice: service.displayPrice?.priceRange?.minPrice ?? undefined,
              maxPrice: service.displayPrice?.priceRange?.maxPrice ?? undefined,
              isActive: service.isActive,
              serviceCode: service.serviceCode,
              allowOnlineBooking: service.allowOnlineBooking,
              sortOrder: 0,
              optionGroups: service.serviceOptionGroups || [],
            })
          );

        acc[category.id] = categoryServices;
        return acc;
      },
      {} as Record<string, ServiceRowData[]>
    );
  }, [categories, services]);

  // Transform Apollo error to standardized error
  const error = useMemo(() => {
    if (!apolloError) return null;

    const standardError = transformApolloError(apolloError);
    logError(standardError, "useServicesQuery");
    return standardError;
  }, [apolloError]);

  // Retry function with error handling
  const retry = useCallback(() => {
    apolloRefetch().catch((retryError) => {
      const standardError = transformApolloError(retryError);
      logError(standardError, "useServicesQuery:retry");
    });
  }, [apolloRefetch]);

  // Refetch function (returns promise for advanced use cases)
  const refetch = useCallback(() => {
    return apolloRefetch();
  }, [apolloRefetch]);

  return {
    categories,
    services,
    servicesByCategory,
    loading,
    error,
    retry,
    refetch,
  };
};
